import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from "react-google-recaptcha";
import { sendMessageToGroq, parseScrollAction, stripActionFromText, isInjectionAttempt, isPrivacyViolation } from '../services/groqService';
import { scrollToSection } from '../Section/ScrollToSection';

const STORAGE_KEY = 'ritesh_portfolio_chat_history';
const MAX_STORED_MESSAGES = 40;

// ─── Contact Flow State ────────────────────────────────────────────────────────
const CONTACT_STEPS = ['name', 'email', 'phone', 'reason', 'confirm'];
const CONTACT_PROMPTS = {
  name: "Sure! Let me help connect you with Ritesh. What's your full name? 👋",
  email: "Got it! What's your email address?",
  phone: "Thanks! Your phone number?",
  reason: "And briefly, what's this about? (e.g., freelance project, collaboration, etc.)",
  confirm: null, // generated dynamically
};

// ─── Context-Aware Suggestion Chips ───────────────────────────────────────────
function getSuggestions(messages) {
  if (messages.length <= 1) {
    return ["⚡ Quick summary of Ritesh", "🛠️ Show me his projects", "💼 I want to hire him", "🎓 What skills does he have?", "🏢 Tell me about InnoSparkMedia"];
  }
  const lastContent = messages[messages.length - 1]?.content?.toLowerCase() || '';
  if (lastContent.includes('project')) {
    return ["🔍 Filter by React", "🌐 Show WordPress projects", "📂 Open Projects section"];
  }
  if (lastContent.includes('contact') || lastContent.includes('hire') || lastContent.includes('email')) {
    return ["📋 Start contact form", "📞 View contact section"];
  }
  if (lastContent.includes('skill') || lastContent.includes('tech')) {
    return ["🌀 Open Skills section", "💼 I want to hire him"];
  }
  return ["⚡ Quick summary", "🛠️ His latest projects", "📬 Get in touch"];
}

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi, I'm Luna! 🌙 I'm Ritesh's AI assistant. I can tell you about his projects, skills, or even help you get in touch with him. How can I brighten your day? ✨",
  timestamp: Date.now(),
  id: 'welcome'
};

// ─── Individual Message Bubble ─────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  const isWarning = msg.type === 'warning';
  const isSuccess = msg.type === 'success';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
    >
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-[10px] font-bold text-white mr-2 mt-1 shrink-0">
          R
        </div>
      )}
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-indigo-600 text-white rounded-br-sm'
            : isWarning
            ? 'bg-red-950/80 border border-red-500/50 text-red-300 rounded-bl-sm'
            : isSuccess
            ? 'bg-green-950/80 border border-green-500/40 text-green-300 rounded-bl-sm'
            : 'bg-white/5 border border-white/10 text-gray-200 rounded-bl-sm'
        }`}
      >
        {isWarning && <span className="block text-red-400 font-bold text-[10px] font-mono tracking-widest mb-1">⚠ SECURITY ALERT</span>}
        {isSuccess && <span className="block text-green-400 font-bold text-[10px] font-mono tracking-widest mb-1">✅ SUCCESS</span>}
        {msg.content}
      </div>
    </motion.div>
  );
}

// ─── Typing Indicator ──────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-[10px] font-bold text-white mr-2 mt-1 shrink-0">R</div>
      <div className="bg-white/5 border border-white/10 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-indigo-400" animate={{ y: [0, -5, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />
        ))}
      </div>
    </div>
  );
}

// ─── Main Chatbot Component ────────────────────────────────────────────────────
export default function PortfolioChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(window.location.hostname === 'localhost');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBadge, setShowBadge] = useState(true);

  // Contact Flow State
  const [contactFlow, setContactFlow] = useState(null); // null | { step, data: {} }
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatWindowRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Handle Click Outside to Close ──
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatWindowRef.current && !chatWindowRef.current.contains(event.target) && isOpen) {
        const toggleButton = document.querySelector('.chat-toggle-button');
        if (toggleButton && !toggleButton.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ── Load from localStorage on mount ──
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          setShowBadge(false);
          return;
        }
      }
    } catch (e) { /* ignore */ }
    setMessages([WELCOME_MESSAGE]);
  }, []);

  // ── Persist to localStorage ──
  useEffect(() => {
    if (messages.length === 0) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
    } catch (e) { /* ignore */ }
  }, [messages]);

  // ── Auto scroll to bottom ──
  useEffect(() => {
    if (isOpen || messages.length > 0 || isLoading) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages, isLoading, isOpen]);

  // ── 🛡️ Google Invisible reCAPTCHA Verification ──
  const recaptchaRef = useRef();
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

  const handleCaptchaVerify = (value) => {
    if (value) {
      setIsVerified(true);
      setShowBadge(false);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  };

  // Execute invisible captcha when chat window opens (only if not already verified via localhost bypass)
  useEffect(() => {
    if (isOpen && !isVerified) {
      const timer = setTimeout(() => {
        try {
          if (recaptchaRef.current) {
            recaptchaRef.current.execute();
          }
        } catch (e) { 
          console.warn("ReCAPTCHA execution failed, bypassing for accessibility...");
          setIsVerified(true); 
        }
      }, 1500); // Give a bit more time for the script to load
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVerified]);

  // ── Focus input when verified ──
  useEffect(() => {
    if (isOpen && isVerified) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setShowBadge(false);
    }
  }, [isOpen, isVerified]);

  // ── Add a bot message helper ──
  const addBotMessage = useCallback((content, type = 'assistant') => {
    setMessages(prev => [...prev, { role: 'assistant', content, type, timestamp: Date.now(), id: Date.now() + Math.random() }]);
  }, []);

  // ── Submit Contact via EmailJS ──
  const submitContactForm = useCallback(async (data) => {
    setIsSubmittingContact(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_nstqt4a',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_m82im7d',
        {
          user_name: data.name,
          user_email: data.email,
          user_phone: data.phone,
          message: data.reason,
          subjects: 'Chat Inquiry via Luna',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'yvVLPzVITIlHLG2Zt'
      );
      addBotMessage("✅ Sent! Ritesh will get back to you very soon. Thanks for reaching out! 🎉✨", 'success');
    } catch (err) {
      addBotMessage("Hmm, I couldn't send that. Please try the Contact Form directly — it's a quick scroll away! 📧", 'assistant');
    } finally {
      setIsSubmittingContact(false);
      setContactFlow(null);
    }
  }, [addBotMessage]);

  // ── Handle Contact Flow Step ──
  const handleContactFlowStep = useCallback(async (userText) => {
    const { step, data } = contactFlow;

    // ── Command / Non-data Detection ──
    // If user enters something that looks like a command rather than valid data, break out
    const looksLikeCommand = /(\?|^(show|go|take|what|how|who|where|tell|list|open|navigate|can you|please|help|cancel|stop|abort|quit|skip|bye|no thanks|nevermind|exit))/i.test(userText.trim());
    const isExplicitCancel = /^(cancel|stop|abort|quit|nevermind|exit|skip|no thanks|bye)$/i.test(userText.trim());

    if (looksLikeCommand || isExplicitCancel) {
      setContactFlow(null);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Contact form cancelled.',
        type: 'warning',
        timestamp: Date.now(),
        id: Date.now() + Math.random(),
      }]);

      if (!isExplicitCancel) {
        // Process as a normal command via Groq
        setIsLoading(true);
        try {
          const contextMessages = messages
            .filter(m => m.role === 'user' || m.role === 'assistant')
            .slice(-10)
            .map(({ role, content }) => ({ role, content }));
          contextMessages.push({ role: 'user', content: userText });
          const responseText = await sendMessageToGroq(contextMessages);
          const action = parseScrollAction(responseText);
          const cleanText = stripActionFromText(responseText);
          setMessages(prev => [...prev, { role: 'assistant', content: cleanText, timestamp: Date.now(), id: Date.now() + 1 }]);
          if (action) {
            setTimeout(() => {
              if (action.action === 'navigate') {
                const url = action.filter && action.filter !== 'all' ? `${action.target}?filter=${action.filter}` : action.target;
                navigate(url);
              } else if (action.action === 'scroll') {
                scrollToSection(action.target);
              }
            }, 600);
          }
        } catch (e) {
          addBotMessage("Oops, had a hiccup! Please try again.");
        } finally {
          setIsLoading(false);
        }
      } else {
        addBotMessage("No problem! Anything else I can help with? ✨");
      }
      return;
    }

    const nextData = { ...data };

    if (step === 'name') {
      // Validate: must look like an actual name (letters + spaces, 2-50 chars, no numbers)
      if (!/^[a-zA-Z\s'-]{2,50}$/.test(userText.trim())) {
        addBotMessage("Hmm, that doesn't look like a name! Could you tell me your full name? 🙂");
        return;
      }
      nextData.name = userText.trim();
      setContactFlow({ step: 'email', data: nextData });
      addBotMessage(`Nice to meet you, ${nextData.name.split(' ')[0]}! 👋  What's your email address? 📧`);
    } else if (step === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userText.trim())) {
        addBotMessage("That doesn't look like a valid email. Can you double-check it? 📧");
        return;
      }
      nextData.email = userText.trim();
      setContactFlow({ step: 'phone', data: nextData });
      addBotMessage("Got it! 📱 Your phone number? (Or type 'skip' to skip)");
    } else if (step === 'phone') {
      const isSkip = /^(skip|none|na|n\/a|-)$/i.test(userText.trim());
      if (!isSkip && !/^[\+]?[\d\s\-\(\)]{7,20}$/.test(userText.trim())) {
        addBotMessage("That doesn't look like a valid phone number. Try again, or type 'skip' to skip! 📱");
        return;
      }
      nextData.phone = isSkip ? 'Not provided' : userText.trim();
      setContactFlow({ step: 'reason', data: nextData });
      addBotMessage("Perfect! 📝 Briefly, what's this about? (e.g. freelance project, collaboration...)");
    } else if (step === 'reason') {
      if (userText.trim().length < 5) {
        addBotMessage("Can you say a bit more about what you're after? Even a short sentence works! 😊");
        return;
      }
      nextData.reason = userText.trim();
      setContactFlow({ step: 'confirm', data: nextData });
      const summary = `Here's what I've got — want me to send this to Ritesh? 📨\n\n📛 Name: ${nextData.name}\n📧 Email: ${nextData.email}\n📱 Phone: ${nextData.phone}\n📝 Message: ${nextData.reason}\n\nReply "yes" to send, or "cancel" to stop.`;
      addBotMessage(summary);
    } else if (step === 'confirm') {
      const yes = /^(yes|yeah|yep|send|submit|ok|sure|go ahead|do it)/i.test(userText.trim());
      const no = /^(no|cancel|stop|nope|don't|dont|abort)/i.test(userText.trim());
      if (yes) {
        addBotMessage("Perfect! Sending your message now... 📡");
        submitContactForm(contactFlow.data);
      } else if (no) {
        setContactFlow(null);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '❌ Contact form cancelled. Your details have been discarded.',
          type: 'warning',
          timestamp: Date.now(),
          id: Date.now() + Math.random(),
        }]);
        setTimeout(() => addBotMessage("No worries! Anything else I can help with? ✨"), 300);
      } else {
        addBotMessage('Just reply "yes" to send or "cancel" to stop. 😊');
      }
    }
  }, [contactFlow, addBotMessage, submitContactForm, messages, navigate]);


  // ── Main Send Handler ──
  const sendMessage = useCallback(async (text) => {
    const userText = (text || input).trim();
    if (!userText || isLoading || !isVerified) return;

    const userMsg = { role: 'user', content: userText, timestamp: Date.now(), id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setTimeout(() => inputRef.current?.focus(), 10);

    // 1. Injection check (client-side, immediate red warning)
    if (isInjectionAttempt(userText)) {
      setIsLoading(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I detected a potential code injection in your message. This has been blocked and flagged. Please keep our conversation respectful and relevant.",
        type: 'warning',
        timestamp: Date.now(),
        id: Date.now() + 1,
      }]);
      return;
    }

    // 2. Privacy violation check
    if (isPrivacyViolation(userText)) {
      setIsLoading(false);
      addBotMessage("I'm not able to share private technical details about the portfolio's internals, credentials, or architecture. Is there something else I can help you with? 😊");
      return;
    }

    // 3. Contact flow step
    if (contactFlow) {
      setIsLoading(false);
      handleContactFlowStep(userText);
      return;
    }


    // 5. Send to Groq
    try {
      const contextMessages = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .concat(userMsg)
        .slice(-12)
        .map(({ role, content }) => ({ role, content }));

      const responseText = await sendMessageToGroq(contextMessages);
      const action = parseScrollAction(responseText);
      const cleanText = stripActionFromText(responseText);

      setMessages(prev => [...prev, { role: 'assistant', content: cleanText, timestamp: Date.now(), id: Date.now() + 1 }]);

      if (action) {
        setTimeout(() => {
          if (action.action === 'navigate') {
            const url = action.filter && action.filter !== 'all' 
              ? `${action.target}?filter=${action.filter}` 
              : action.target;
            navigate(url);
          } else if (action.action === 'scroll') {
            if (location.pathname !== '/') {
              navigate(`/#${action.target}`);
              setTimeout(() => scrollToSection(action.target), 100);
            } else {
              scrollToSection(action.target);
            }
          } else if (action.action === 'start_contact') {
            setContactFlow({ step: 'name', data: {} });
            setTimeout(() => addBotMessage(CONTACT_PROMPTS.name), 200);
            // Also scroll user to the contact form section
            setTimeout(() => scrollToSection('Contact'), 400);
          }
        }, 800);
      }
      
      // Auto-focus input after sending
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Chatbot Error:', error);
      addBotMessage("Oops, I had a little hiccup connecting there. Please try again!");
    } finally {
      setIsLoading(false);
    }
  }, [input, messages, isLoading, contactFlow, handleContactFlowStep, addBotMessage, isVerified]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearHistory = () => {
    localStorage.removeItem(STORAGE_KEY);
    setMessages([WELCOME_MESSAGE]);
    setContactFlow(null);
  };

  const suggestions = getSuggestions(messages);
  const showSuggestions = messages.length <= 2 && !isLoading && !contactFlow && isVerified;

  // ── Quick-start contact from chip ──
  const handleSuggestion = (s) => {
    setMessages(prev => [...prev, { role: 'user', content: s, timestamp: Date.now(), id: Date.now() }]);
    sendMessage(s);
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-3">
        {/* Hidden reCAPTCHA Widget */}
        <div style={{ display: 'none' }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={siteKey}
            onChange={handleCaptchaVerify}
            theme="dark"
          />
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={chatWindowRef}
              key="chat-window"
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-[calc(100vw-32px)] sm:w-[400px] max-w-[400px] bg-[#0c0d10] border border-white/10 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden mb-2"
              style={{ maxHeight: 'min(620px, 78vh)' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-gradient-to-r from-indigo-950/50 to-purple-950/30 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-black text-white shadow-lg">L</div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#0c0d10]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">Luna</p>
                    <p className="text-gray-500 text-[10px] font-mono tracking-wider">Ritesh's AI • Online</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={clearHistory} className="text-gray-600 hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-white/5" title="Clear chat">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                  <button onClick={() => setIsOpen(false)} className="text-gray-600 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>

              {/* Verified Content Area */}
              {!isVerified && (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    <svg className="w-5 h-5 text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-400 text-xs font-mono tracking-wider">SECURE CONNECTION STARTING...</p>
                </div>
              )}

              {isVerified && (
                <>
                  {/* Contact Flow Banner */}
                  {contactFlow && (
                    <div className="px-4 py-2 bg-indigo-950/50 border-b border-indigo-500/20 flex items-center justify-between shrink-0">
                      <span className="text-indigo-300 text-[10px] font-mono tracking-wider uppercase">📋 Contact Flow Active — Step {CONTACT_STEPS.indexOf(contactFlow.step) + 1}/{CONTACT_STEPS.length}</span>
                      <button onClick={() => {
                        setContactFlow(null);
                        setMessages(prev => [...prev, {
                          role: 'assistant',
                          content: '❌ Contact form cancelled.',
                          type: 'warning',
                          timestamp: Date.now(),
                          id: Date.now() + Math.random(),
                        }]);
                        setTimeout(() => addBotMessage("Anything else I can help with? ✨"), 300);
                      }} className="text-gray-600 hover:text-red-400 text-[10px] font-mono transition-colors">Cancel</button>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 scrollbar-thin scrollbar-thumb-white/10">
                    {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
                    {(isLoading || isSubmittingContact) && <TypingIndicator />}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Suggestion Chips */}
                  {showSuggestions && (
                    <div className="px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-none shrink-0">
                      {suggestions.map((s, i) => (
                        <button key={i} onClick={() => handleSuggestion(s)} className="shrink-0 px-3 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-[10px] font-mono tracking-wider hover:bg-indigo-500/20 transition-colors whitespace-nowrap">
                          {s}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Input */}
                  <div className="px-4 py-3 border-t border-white/5 shrink-0 bg-[#0c0d10]">
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5">
                      <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={contactFlow ? `Your ${contactFlow.step === 'reason' ? 'message' : contactFlow.step}...` : "Ask me about Ritesh..."}
                        className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none"
                        disabled={isLoading || isSubmittingContact}
                      />
                      <button
                        onClick={() => sendMessage()}
                        disabled={!input.trim() || isLoading || isSubmittingContact}
                        className="w-7 h-7 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all shrink-0"
                      >
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M12 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* FAB Button */}
        <motion.button
          onClick={() => setIsOpen(prev => !prev)}
          className="chat-toggle-button relative w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 text-white shadow-[0_8px_30px_rgba(79,70,229,0.5)] hover:shadow-[0_8px_40px_rgba(79,70,229,0.7)] flex items-center justify-center transition-shadow duration-300"
          whileHover={{ scale: 1.07 }}
          whileTap={{ scale: 0.93 }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.svg key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }} className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </motion.svg>
            ) : (
              <motion.svg key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }} className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </motion.svg>
            )}
          </AnimatePresence>

          {/* Unread Badge */}
          {showBadge && !isOpen && (
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white">1</motion.span>
          )}
        </motion.button>
      </div>
    </>
  );
}
